import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Checkbox, Select, Space, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type {
  LocalCoordinatorSettingsResponse,
  LocalCoordinatorSettingsValues,
  NotificationsResponse,
} from "../api/types";

/**
 * The two controls the old toolbars carried over every list of speakers a
 * coordinator picks from: in what order the candidates appear, and whether the
 * congregation's own speakers appear at all.
 *
 * What they set belongs to the coordinator, not to the list in front of them —
 * the server reads both off that record while building any of these lists. So a
 * choice made here holds for the next week, for the other list, and shows up on
 * the settings screen; and every list this app has is asked for again once it
 * changes.
 */
export function SpeakerListPreferences({ disabled = false }: { disabled?: boolean }) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ["congregation", "local_coordinator_settings"],
    queryFn: () =>
      apiFetch<LocalCoordinatorSettingsResponse>("/congregation/local_coordinator_settings"),
  });

  // The PUT takes each field on its own, so one preference can be written
  // without touching the rest of the coordinator's settings. Deliberately
  // silent about its success notification: a toast on every change of sort
  // order would be noise, and the list re-appearing is the answer.
  const preference = useMutation({
    mutationFn: (values: Partial<LocalCoordinatorSettingsValues>) =>
      apiFetch<NotificationsResponse>("/congregation/local_coordinator_settings", {
        method: "PUT",
        body: values,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["congregation", "local_coordinator_settings"],
      });
      // Every candidate list of this screen family is built from those two
      // values, so they all have to be asked for again.
      void queryClient.invalidateQueries({ queryKey: ["local_assignation"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const busy = disabled || preference.isPending;
  const coordinator = settings.data?.data.local_coordinator;

  return (
    <Space size="middle" wrap style={{ marginBottom: 16 }}>
      <Space size={8}>
        <Typography.Text type="secondary">{t("local_assignation.order")}</Typography.Text>
        <Tooltip title={t("local_coordinator.hints.speaker_speech_sorting_mode")}>
          <Select
            size="small"
            style={{ width: 280 }}
            loading={settings.isPending}
            disabled={busy}
            value={coordinator?.speaker_speech_sorting_mode ?? undefined}
            options={settings.data?.data.sorting_modes ?? []}
            onChange={(value: number) => preference.mutate({ speaker_speech_sorting_mode: value })}
          />
        </Tooltip>
      </Space>

      <Tooltip title={t("local_coordinator.hints.hide_own_speakers")}>
        <Checkbox
          checked={coordinator?.hide_own_speakers ?? false}
          disabled={busy || settings.isPending}
          onChange={(event) => preference.mutate({ hide_own_speakers: event.target.checked })}
        >
          {t("local_assignation.order_hide_own_speakers")}
        </Checkbox>
      </Tooltip>
    </Space>
  );
}
